import type { MessageId, ThreadId } from "@cleo/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useRef } from "react";
import { fetchPrimary } from "../environments/primary/fetch";

import { randomUUID } from "../lib/utils";
import { toastManager } from "../components/ui/toast";
import { sessionForkPoint } from "../components/chat/forkFromHere.logic";
import { roomUniverseCoordinateKey, useRoomUniverseStore } from "./roomUniverseStore";
import { recordWorldSessionBirthAttempt } from "./worldSessionBirthAttemptsClient";
import { openWorldSession, orderContextBirthSelection } from "./worldSessionClient";
import {
  prepareForkWorldSessionExecutionBirth,
  type WorldForkSourceExecution,
} from "./worldSessionExecutionBirth";
import { useWorldSessionExecutionDispatch } from "./useWorldSessionLaunchContext";
import { useWorldViewIfSeated } from "./worldView";

export interface SessionForkInput {
  readonly worldKey: string;
  readonly sourceSessionId: string;
  readonly source: WorldForkSourceExecution;
  /**
   * Can 46: the message the fork carries the conversation through ("Fork from
   * here"). Absent means the latest settled message ("Fork session").
   */
  readonly throughMessageId?: MessageId;
}

/**
 * The server names a non-retiring snapshot `capture-<sha256>`
 * (conversationArchiveStorage.ts, conversationArchiveSnapshotId); the World
 * authority checks the same spelling (worldContextBirth.js,
 * requireForkSnapshotRef). One producer, one spelling (W-7171f6dc).
 */
const SNAPSHOT_ID = /^capture-[0-9a-f]{64}$/u;

function exactSnapshotMember(input: {
  readonly body: unknown;
  readonly roomId: string;
  readonly sessionId: string;
  readonly threadId: string;
}): string | null {
  if (typeof input.body !== "object" || input.body === null || Array.isArray(input.body))
    return null;
  const body = input.body as Record<string, unknown>;
  const memberId = body["memberId"];
  const snapshotId = body["snapshotId"];
  if (
    body["ok"] !== true ||
    typeof memberId !== "string" ||
    typeof snapshotId !== "string" ||
    !SNAPSHOT_ID.test(snapshotId)
  ) {
    return null;
  }
  const expected = `world-r2:context/sessions/${input.roomId}/${input.sessionId}/${input.threadId}/snapshots/${snapshotId}.md`;
  return memberId === expected ? memberId : null;
}

async function snapshotSource(input: {
  readonly roomId: string;
  readonly sessionId: string;
  readonly threadId: string;
}): Promise<string | null> {
  try {
    const response = await fetchPrimary("/api/neucleos/session/snapshot", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ threadId: input.threadId }),
    });
    const body: unknown = await response.json();
    return response.ok ? exactSnapshotMember({ body, ...input }) : null;
  } catch {
    return null;
  }
}

type PendingFork = {
  readonly input: Parameters<typeof openWorldSession>[0];
  readonly prepared: Extract<
    ReturnType<typeof prepareForkWorldSessionExecutionBirth>,
    { readonly _tag: "Launch" }
  >;
  uncertain: boolean;
  opened: Extract<Awaited<ReturnType<typeof openWorldSession>>, { readonly ok: true }> | null;
};

/** BD4 — snapshot without retirement, then open and launch a sibling Session. */
export function useSessionFork(): (input: SessionForkInput) => Promise<ThreadId | null> {
  const pending = useRef(new Map<string, PendingFork>());
  const inFlight = useRef(new Map<string, Promise<ThreadId | null>>());
  const view = useWorldViewIfSeated();
  const navigate = useNavigate();
  const { dispatch, executionArrived } = useWorldSessionExecutionDispatch();
  return useCallback(
    async ({ worldKey, sourceSessionId, source, throughMessageId }) => {
      const ready =
        view?.state.kind === "world-ready" && view.state.worldKey === worldKey ? view.state : null;
      const sourceSession = ready?.index.sessions.find(
        (candidate) => candidate.sessionId === sourceSessionId,
      );
      if (
        ready === null ||
        sourceSession === undefined ||
        source.worldKey !== worldKey ||
        source.sessionId !== sourceSessionId ||
        source.archivedAt !== null
      ) {
        toastManager.add({
          type: "error",
          title: "The session could not be forked",
          description: "The source session is not available at this World address.",
        });
        return null;
      }
      const coordinate = {
        worldKey,
        roomId: sourceSession.roomId,
        sessionId: sourceSessionId,
        agentEnvironmentId: String(source.environmentId),
        agentThreadId: String(source.id),
      };
      const forkPoint =
        throughMessageId === undefined
          ? sessionForkPoint(source)
          : { ok: true as const, messageId: throughMessageId };
      if (!forkPoint.ok) {
        toastManager.add({
          type: "error",
          title: "The session could not be forked",
          description: forkPoint.reason,
        });
        return null;
      }
      // One pending fork per source execution AND fork point: forking the
      // same conversation from another message is a different fork.
      const key = `${roomUniverseCoordinateKey(coordinate)}\u0000${forkPoint.messageId ?? ""}`;
      const active = inFlight.current.get(key);
      if (active) return active;
      const resume = async (batch: PendingFork): Promise<ThreadId | null> => {
        const { prepared } = batch;
        const forkSessionId = batch.input.sessionId;
        if (batch.opened === null) {
          const reserved = await recordWorldSessionBirthAttempt({
            worldKey,
            sessionId: forkSessionId,
            environmentId: prepared.environmentId,
            input: prepared.input,
          });
          if (!reserved.ok) {
            toastManager.add({
              type: "error",
              title: "The session could not be forked",
              description: "The fork execution could not be reserved. Retry uses this same fork.",
            });
            return null;
          }
          let opened: Awaited<ReturnType<typeof openWorldSession>>;
          try {
            opened = await openWorldSession(batch.input);
          } catch {
            opened = {
              ok: false,
              kind: "transport",
              reason: "The fork could not be confirmed. Retry uses this same fork.",
            };
          }
          if (!opened.ok) {
            if (opened.kind === "context_changed" && !batch.uncertain) pending.current.delete(key);
            else batch.uncertain = true;
            toastManager.add({
              type: "error",
              title: "The session could not be forked",
              description: opened.reason,
            });
            return null;
          }
          batch.opened = opened;
        }
        // The create may have committed despite a lost local acknowledgment.
        // Check the exact reserved shell; automatic launch adopts that identity.
        try {
          await dispatch(prepared.environmentId, prepared.input);
        } catch {
          /* read back below */
        }
        const arrived = await executionArrived(
          prepared.environmentId,
          prepared.input.threadId,
        ).catch(() => false);
        if (!arrived)
          toastManager.add({
            type: "warning",
            title: "The forked session is open",
            description: "Its execution is still starting with the same recorded identity.",
          });
        view?.acceptEntryReadback(batch.opened.entry);
        await navigate({
          to: "/session/$sessionId",
          params: { sessionId: forkSessionId },
        } as never);
        pending.current.delete(key);
        return prepared.input.threadId;
      };
      const operation = (async () => {
        const saved = pending.current.get(key);
        if (saved) return resume(saved);
        const entry =
          useRoomUniverseStore.getState().byCoordinate[roomUniverseCoordinateKey(coordinate)];
        if (
          entry?.state !== "complete" &&
          entry?.state !== "paged" &&
          entry?.state !== "selection"
        ) {
          toastManager.add({
            type: "error",
            title: "The session could not be forked",
            description: "Its current Context selection is not readable yet.",
          });
          return null;
        }
        return orderContextBirthSelection<ThreadId | null>(
          entry,
          async (contextBirth) => {
            const snapshotRef = await snapshotSource({
              roomId: sourceSession.roomId,
              sessionId: sourceSessionId,
              threadId: String(source.id),
            });
            if (snapshotRef === null) {
              toastManager.add({
                type: "error",
                title: "The session could not be forked",
                description: "The non-retiring source snapshot was not confirmed.",
              });
              return null;
            }
            const forkSessionId = `S_${randomUUID().replaceAll("-", "")}`;
            const label = `Fork · ${source.title}`;
            const prepared = prepareForkWorldSessionExecutionBirth({
              worldKey,
              sourceSessionId,
              forkSessionId,
              label,
              source,
              throughMessageId: forkPoint.messageId,
            });
            if (prepared._tag === "Refused") {
              toastManager.add({
                type: "error",
                title: "The session could not be forked",
                description: "The source execution profile is not launchable.",
              });
              return null;
            }
            const batch: PendingFork = {
              prepared,
              uncertain: false,
              opened: null,
              input: {
                worldKey,
                sessionId: forkSessionId,
                label,
                parentId: sourceSession.roomId,
                contextBirth,
                forkedFrom: {
                  sessionId: sourceSessionId,
                  threadId: String(source.id),
                  snapshotRef,
                },
                ...(source.workroomKey == null ? {} : { workroomKey: source.workroomKey }),
              },
            };
            pending.current.set(key, batch);
            return resume(batch);
          },
          (reason) => {
            toastManager.add({
              type: "error",
              title: "The session could not be forked",
              description: reason,
            });
            return null;
          },
        );
      })();
      inFlight.current.set(key, operation);
      try {
        return await operation;
      } finally {
        inFlight.current.delete(key);
      }
    },
    [dispatch, executionArrived, navigate, view],
  );
}
